import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  ListHomeDetailsRequest, ListHomeDetailsResponse, ProductName, SituViewWebApiClient, StyleConfigTemplate, HomeDetail, ListTagsRequest, ListTagsResponse, Tag,
  UpdateHomeRequest,
  UpdateHomeResponse
} from 'situview-webapi-client';
import { AuthService, StateService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

@Component({
  selector: 'home-details-list',
  templateUrl: './home-details-list.component.html',
  styleUrls: ['./home-details-list.component.less']
})
export class HomeDetailsListComponent extends SituBaseComponent {
  loading: boolean = false;
  data = new MatTableDataSource<HomeDetail>();
  searchTerm = "";
  productNames = ProductName;
  productName: ProductName | undefined;
  //isHidden: boolean | undefined;
  taggedOnly: boolean = false;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private situViewApiClient: SituViewWebApiClient
  ) {
    super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadTags()
      .then(() => {
        this.loadData();
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  downloadCSV() {
    this.loading = true;

    // Fetch fresh data from endpoint (including both enabled and disabled homes)
    let request = new ListHomeDetailsRequest();
    request.companyId = this.selectedCompany?.id;
    // Don't filter by visibility - we want all homes
    // request.isHidden is not set, so we get all homes

    this.situViewApiClient.listHomeDetails(request)
      .then((response: ListHomeDetailsResponse) => {
        if (response.success && response.items) {
          const csvData = this.convertToCSV(response.items);
          const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
          const timestamp = Utils.getCurrentTimestamp();
          const companyName = this.selectedCompany?.name || 'unknown';
          const fileName = `${companyName}_homes_${timestamp}.csv`;

          // For IE 11
          if ((navigator as any).msSaveBlob) {
            (navigator as any).msSaveBlob(blob, fileName);
          } else {
            // Modern browsers
            const link = document.createElement('a');
            if (link.download !== undefined) {
              const url = URL.createObjectURL(blob);
              link.setAttribute('href', url);
              link.setAttribute('download', fileName);
              link.style.visibility = 'hidden';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }
        } else {
          this.showMessage("There was an issue retrieving home details for export!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve home details for export!");
      });
  }  
  private convertToCSV(homeDetails: HomeDetail[]): string {
    let str = '';
    let row = '';

    const headers = ['Home', 'Visible', 'ActiveThisMonth', 'RenderedThisMonth', 'HasRaytraceDraft', 'HasRaytracePlus'];
    row = headers.join(',') + '\r\n';
    str += row;

    // Initialize counters for totals
    let visibleCount = 0;
    let activeCount = 0;
    let renderedThisMonthCount = 0;
    let raytraceDraftCount = 0;
    let raytracePlusCount = 0;

    homeDetails.forEach((item: HomeDetail) => {
      let line = '';

      const isVisible = !item.isHidden;
      const isRenderedThisMonth = this.isRenderedThisMonth(item.dateLastRendered);
      const hasRaytraceDraft = !!item.dateLastRaytracedDraftRendered;
      const hasRaytracePlus = !!item.dateLastRaytracedPlusRendered;
      const isActive = isRenderedThisMonth || hasRaytraceDraft || hasRaytracePlus;

      // Count true values
      if (isVisible) visibleCount++;
      if (isActive) activeCount++;
      if (isRenderedThisMonth) renderedThisMonthCount++;
      if (hasRaytraceDraft) raytraceDraftCount++;
      if (hasRaytracePlus) raytracePlusCount++;

      line += `${item.name},`;
      line += `${isVisible},`;
      line += `${isActive},`;
      line += `${isRenderedThisMonth},`;
      line += `${hasRaytraceDraft},`;
      line += `${hasRaytracePlus}`;
      str += line + '\r\n';
    });

    // Add totals row
    str += `TOTALS,${visibleCount},${activeCount},${renderedThisMonthCount},${raytraceDraftCount},${raytracePlusCount}\r\n`;

    return str;
  }

  getTagLabel(tagId: string): string {
    let tag = this.tags?.find(x => x.id === tagId);
    if (tag) {
      return `${tag.name}`;
    }
    return "";
  }
  
  getTagCategory(tagId: string): string {
    let tag = this.tags?.find(x => x.id === tagId);
    if (tag) {
      return `${tag?.category?.name}`;
    }
    return "";
  }

  onClickStyleTemplate(item: StyleConfigTemplate) {
    this.router.navigate(['admin/style-template', item.id]);
  }

  setProductName(productName: ProductName) {
    this.productName = productName;
    this.loadData();
  }
  
  // setIsHidden(isHidden: boolean) {
  //   this.isHidden = isHidden;
  //   this.loadData();
  // }
  
  setTaggedOnly() {
    this.taggedOnly = !this.taggedOnly;
    this.loadData();
  }

  showOnlyVisible: boolean | undefined = true;
  toggleVisibility() {
    if (this.showOnlyVisible === true) {
      this.showOnlyVisible = undefined;
    } else {
      this.showOnlyVisible = true;
    }
    this.loadData();
  }

  toggleHomeVisibility(home: HomeDetail) {
    if (!home) {
      this.showMessage("Home is required!");
      return;
    }
    this.loading = true;

    let request = new UpdateHomeRequest();
    request.companyId = this.selectedCompany?.id;
    request.homeId = home.id;
    request.isHidden = !home.isHidden;

    this.situViewApiClient.updateHome(request)
      .then((response: UpdateHomeResponse) => {
        if (response.success) {
          home.isHidden = !home.isHidden;
        } else {
          this.showMessage("There was an issue updating home details!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve home details!");
      });
  }

  loadData() {
    this.loading = true;

    let request = new ListHomeDetailsRequest();
    request.companyId = this.selectedCompany?.id;
    if (this.taggedOnly) request.excludeHomesWithNoTags = true;
    if (this.searchTerm) request.searchTerm = this.searchTerm;
    if (this.showOnlyVisible !== undefined) request.isHidden = !this.showOnlyVisible;

    this.situViewApiClient.listHomeDetails(request)
      .then((response: ListHomeDetailsResponse) => {
        if (response.success) {
          this.data.data = response.items ?? [];
        } else {
          this.showMessage("There was an issue retrieving home details!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve home details!");
      });
  }

  tags: Tag[] | undefined;
  private loadTags() {
    let request = new ListTagsRequest();
    request.companyId = this.selectedCompany?.id;

    return this.situViewApiClient.listTags(request)
      .then((response: ListTagsResponse) => {
        if (response.success) {
          this.tags = response.tags;
        }
      })
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  isRenderedThisMonth(date: Date | undefined): boolean {
    if (!date) return false;
    
    const renderDate = new Date(date);
    const now = new Date();
    
    return renderDate.getMonth() === now.getMonth() && 
           renderDate.getFullYear() === now.getFullYear();
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }

  // Helper method to check if home has ANY raytracePlus renders
  hasRaytracePlus(item: HomeDetail): boolean {
    return !!item.dateLastRaytracedPlusRendered;
  }

  // Helper method to check if home has ANY raytraceDraft renders
  hasRaytraceDraft(item: HomeDetail): boolean {
    return !!item.dateLastRaytracedDraftRendered;
  }

  // Helper method to check if home was active/seen this month (independent of visibility toggle)
  isHomeActive(item: HomeDetail): boolean {
    const isRenderedThisMonth = this.isRenderedThisMonth(item.dateLastRendered);
    const hasRaytracePlus = this.hasRaytracePlus(item);
    return isRenderedThisMonth || hasRaytracePlus;
  }

}